import { logger } from '@talos/kyoko';
import { useEffect } from 'react';
import { useLocalStorage } from 'react-use';
import { useClientLogo } from '../components/Logo/useClientLogo';
import { useOrgConfiguration } from '../providers/OrgConfigurationProvider';
import { envConfig } from '../utils/envConfig';
import { CLIENT_FAVICON_KEY } from './consts';

/**
 * Hook that dynamically updates the favicon based on ui.ClientLogoName.
 * Waits for org configs to load before setting the favicon.
 *
 * Priority system:
 * 1. Client logo-derived favicon - when ui.ClientLogoName flag is configured
 * 2. VITE_FAVICON env variable - set at build time for different environments
 * 3. Default favicon - 'favicon.svg' if all else fails
 *
 * Behavior:
 * - Client logo available: Uses client favicon and persists to localStorage
 * - Client logo faulty: Uses empty favicon and clears localStorage
 * - No client logo config: Uses environment favicon and clears localStorage
 */
export function useDynamicFavicon() {
  const { isLoaded: isOrgConfigsLoaded } = useOrgConfiguration();
  const faviconResult = useClientLogo({ collapsed: true, takeBrowserTheme: true });
  const [, setSavedClientFavicon, removeSavedClientFavicon] = useLocalStorage(CLIENT_FAVICON_KEY);

  useEffect(() => {
    if (!isOrgConfigsLoaded) {
      return;
    }

    // Correct client logo is configured
    if (faviconResult.state === 'client') {
      setSavedClientFavicon(faviconResult.filepath);
      setFavicon(faviconResult.filepath);
      return;
    }

    // Client logo config is faulty
    if (faviconResult.state === 'empty') {
      removeSavedClientFavicon();
      setFavicon('favicon-empty.svg');
      return;
    }

    // No client logo config set up => Make sure localStorage is wiped
    removeSavedClientFavicon();

    // Fall back to existing behavior with VITE_FAVICON and a worst-case fallback
    setFavicon(envConfig.VITE_FAVICON || 'favicon.svg');
  }, [isOrgConfigsLoaded, faviconResult, setSavedClientFavicon, removeSavedClientFavicon]);
}

function setFavicon(href: string) {
  const faviconElement = document.getElementById('favicon');

  if (!faviconElement || !(faviconElement instanceof HTMLLinkElement)) {
    logger.warn('[useFavicon] Favicon element with id "favicon" not found in document.');
    return;
  }

  // Add slash if needed
  faviconElement.href = href.startsWith('/') ? href : `/${href}`;
}
